/**
 * 索引色（PNG-8）编码（导出对话框补充，参照 Photoshop「存储为 Web 所用
 * 格式」的 PNG-8）。颜色量化见 palette.ts；这里只管把量化结果按 PNG
 * 规范的色型 3（调色板）打包成合法字节流。
 *
 * 压缩用浏览器原生 `CompressionStream('deflate')`——跟 io/cvsm/zip.ts 的
 * `deflateRaw` 同一个理由（零依赖、规范原生支持），只是这里要 PNG IDAT
 * 需要的 zlib 包装格式（RFC1950），不是 ZIP 用的裸 deflate（RFC1951）。
 */
import { crc32 } from '../cvsm/zip';
import type { Rgba } from './palette';

const PNG_SIGNATURE = Uint8Array.from([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);

async function zlibDeflate(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new CompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

function u32be(value: number): Uint8Array {
  return Uint8Array.from([
    (value >>> 24) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 8) & 0xff,
    value & 0xff,
  ]);
}

function chunk(type: string, data: Uint8Array): Uint8Array {
  const typeBytes = Uint8Array.from(type.split('').map((c) => c.charCodeAt(0)));
  const body = new Uint8Array(typeBytes.length + data.length);
  body.set(typeBytes, 0);
  body.set(data, typeBytes.length);
  const out = new Uint8Array(4 + body.length + 4);
  out.set(u32be(data.length), 0);
  out.set(body, 4);
  out.set(u32be(crc32(body)), 4 + body.length);
  return out;
}

function concat(parts: readonly Uint8Array[]): Uint8Array {
  const total = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

/**
 * 编码索引色 PNG。`indices` 是每像素一个字节的调色板下标（`width*height`
 * 长），`palette` 长度 2–256。每条扫描线固定用 filter 0（None）——
 * 调色板图像的相邻像素在数值上未必接近，1–4 号预测滤波对它没什么帮助，
 * 真实编码器对色型 3 图像也普遍就是这么做的。
 */
export async function encodeIndexedPng(
  indices: Uint8Array,
  palette: readonly Rgba[],
  width: number,
  height: number,
): Promise<Uint8Array> {
  if (indices.length !== width * height) {
    throw new RangeError(`indices 长度 ${indices.length} 与 ${width}×${height} 不符`);
  }
  if (palette.length < 1 || palette.length > 256) {
    throw new RangeError(`调色板长度 ${palette.length} 必须在 1–256 之间`);
  }

  const ihdr = new Uint8Array(13);
  const ihdrView = new DataView(ihdr.buffer);
  ihdrView.setUint32(0, width);
  ihdrView.setUint32(4, height);
  ihdr[8] = 8; // bit depth：始终用整字节索引，不做 <8 的位打包（§文件头注释）
  ihdr[9] = 3; // color type 3 = 调色板
  ihdr[10] = 0; // compression method
  ihdr[11] = 0; // filter method
  ihdr[12] = 0; // interlace method

  const plte = new Uint8Array(palette.length * 3);
  for (let i = 0; i < palette.length; i++) {
    const c = palette[i]!;
    plte[i * 3] = c.r;
    plte[i * 3 + 1] = c.g;
    plte[i * 3 + 2] = c.b;
  }

  const hasAlpha = palette.some((c) => c.a < 255);
  const trns = hasAlpha ? Uint8Array.from(palette.map((c) => c.a)) : null;

  const scanlineBytes = width + 1; // 每行多一个 filter-type 前缀字节
  const raw = new Uint8Array(scanlineBytes * height);
  for (let y = 0; y < height; y++) {
    raw[y * scanlineBytes] = 0; // filter 0
    raw.set(indices.subarray(y * width, (y + 1) * width), y * scanlineBytes + 1);
  }
  const idatData = await zlibDeflate(raw);

  return concat([
    PNG_SIGNATURE,
    chunk('IHDR', ihdr),
    chunk('PLTE', plte),
    ...(trns === null ? [] : [chunk('tRNS', trns)]),
    chunk('IDAT', idatData),
    chunk('IEND', new Uint8Array(0)),
  ]);
}
